<script>
import { getPresignedUrl, uploadAttachment } from '@/api/attachment.js'
import { summaryText } from '@/constants/imageSpecs.js'
import {
  createMediaHubMainBanner,
  deleteMediaHubMainBanner,
  getMediaHubMainBanners,
  updateMediaHubMainBanner,
} from '@/api/mediaHub.js'

const YN_OPTIONS = [
  { title: '노출', value: 'Y' },
  { title: '미노출', value: 'N' },
]

export default {
  name: 'MediaHubMainBannerPage',
  data() {
    return {
      banners: [],
      loading: false,
      dialog: false,
      saving: false,
      uploadFile: null,
      previewUrl: '',
      editTarget: null,
      form: this.emptyForm(),
      ynOptions: YN_OPTIONS,
    }
  },
  computed: {
    imageSpec() { return summaryText('MEDIAHUB_MAIN_BANNER') },
    dialogTitle() {
      return this.editTarget ? '메인 배너 수정' : '메인 배너 등록'
    },
  },
  mounted() {
    this.fetchBanners()
  },
  methods: {
    emptyForm() {
      return {
        bannerTitle: '',
        bannerSubtitle: '',
        imageUrl: '',
        storageKey: '',
        altText: '',
        exposeYn: 'Y',
        exposeOrder: 0,
        displaySeconds: 30,
      }
    },
    async fetchBanners() {
      this.loading = true
      try {
        const res = await getMediaHubMainBanners()
        if (res.data?.code === 0) {
          const rows = res.data.data || []
          this.banners = await Promise.all(rows.map(async (item) => ({
            ...item,
            displayImageUrl: await this.resolvePreviewUrl(item.storageKey, item.imageUrl),
          })))
        }
      } finally {
        this.loading = false
      }
    },
    openCreate() {
      this.editTarget = null
      this.form = this.emptyForm()
      this.clearPendingImage()
      this.dialog = true
    },
    async openEdit(item) {
      this.editTarget = item
      this.form = {
        bannerTitle: item.bannerTitle || '',
        bannerSubtitle: item.bannerSubtitle || '',
        imageUrl: item.imageUrl || '',
        storageKey: item.storageKey || '',
        altText: item.altText || '',
        exposeYn: item.exposeYn || 'Y',
        exposeOrder: item.exposeOrder || 0,
        displaySeconds: item.displaySeconds || 30,
      }
      this.clearPendingImage()
      this.previewUrl = await this.resolvePreviewUrl(item.storageKey, item.imageUrl)
      this.dialog = true
    },
    selectImage(event) {
      const file = event.target.files?.[0]
      event.target.value = ''
      if (!file) return
      this.clearPendingImage()
      this.uploadFile = file
      this.previewUrl = URL.createObjectURL(file)
      this.form.altText = this.form.altText || file.name
    },
    async uploadPendingImage() {
      if (!this.uploadFile) return
      const res = await uploadAttachment(this.buildUploadPath(), this.uploadFile)
      if (res.data?.code === 0) {
        this.form.storageKey = res.data.objectKey
        this.form.imageUrl = res.data.url
      }
    },
    buildUploadPath() {
      const now = new Date()
      const year = now.getFullYear()
      const month = String(now.getMonth() + 1).padStart(2, '0')
      const day = String(now.getDate()).padStart(2, '0')
      return `goldnara/mediahub/main-banner/${year}/${month}/${day}/`
    },
    async saveBanner() {
      if (!this.form.bannerTitle) return
      this.saving = true
      try {
        await this.uploadPendingImage()
        const payload = {
          ...this.form,
          exposeOrder: Number(this.form.exposeOrder || 0),
          displaySeconds: Number(this.form.displaySeconds || 30),
        }
        const res = this.editTarget
          ? await updateMediaHubMainBanner(this.editTarget.mediaHubMainBannerCode, payload)
          : await createMediaHubMainBanner(payload)
        if (res.data?.code === 0) {
          this.dialog = false
          await this.fetchBanners()
        }
      } finally {
        this.saving = false
      }
    },
    async removeBanner(item) {
      if (!window.confirm('메인 배너를 미노출 처리하시겠습니까?')) return
      const res = await deleteMediaHubMainBanner(item.mediaHubMainBannerCode)
      if (res.data?.code === 0) {
        await this.fetchBanners()
      }
    },
    removeImage() {
      this.clearPendingImage()
      this.form.imageUrl = ''
      this.form.storageKey = ''
      this.previewUrl = ''
    },
    clearPendingImage() {
      if (this.previewUrl?.startsWith('blob:')) {
        URL.revokeObjectURL(this.previewUrl)
      }
      this.uploadFile = null
    },
    async resolvePreviewUrl(storageKey, imageUrl) {
      if (!storageKey) return imageUrl || ''
      try {
        const res = await getPresignedUrl(storageKey)
        if (res.data?.code === 0 && res.data.presignedUrl) return res.data.presignedUrl
      } catch (e) {
        return imageUrl || ''
      }
      return imageUrl || ''
    },
    formatDate(value) {
      if (!value) return '-'
      return new Intl.DateTimeFormat('ko-KR', {
        year: 'numeric',
        month: '2-digit',
        day: '2-digit',
        hour: '2-digit',
        minute: '2-digit',
      }).format(new Date(value))
    },
  },
}
</script>

<template>
  <div>
    <div class="d-flex align-center justify-space-between mb-5">
      <div>
        <h1 class="text-h5 font-weight-bold mb-1">미디어허브 메인배너 관리</h1>
        <div class="text-body-2 text-grey">사용자 미디어허브 메인 상단에 노출되는 배너를 관리합니다.</div>
      </div>
      <v-btn color="primary" prepend-icon="mdi-plus" @click="openCreate">메인 배너 등록</v-btn>
    </div>

    <v-card rounded="lg">
      <v-card-title class="d-flex align-center ga-2">
        배너 목록
        <v-chip size="small" color="primary" variant="tonal">{{ banners.length }}건</v-chip>
      </v-card-title>
      <v-data-table
        :headers="[
          { title: '이미지', key: 'imageUrl', sortable: false, width: 160 },
          { title: '제목', key: 'bannerTitle' },
          { title: '노출', key: 'exposeYn', width: 110 },
          { title: '순서', key: 'exposeOrder', width: 100 },
          { title: '노출시간', key: 'displaySeconds', width: 120 },
          { title: '등록일', key: 'createdDt', width: 170 },
          { title: '관리', key: 'actions', sortable: false, width: 130 },
        ]"
        :items="banners"
        :loading="loading"
        item-value="mediaHubMainBannerCode"
        density="comfortable"
      >
        <template #item.imageUrl="{ item }">
          <div class="banner-thumb">
            <img v-if="item.displayImageUrl" :src="item.displayImageUrl" alt="" />
            <v-icon v-else color="grey">mdi-image-off-outline</v-icon>
          </div>
        </template>
        <template #item.bannerTitle="{ item }">
          <div class="font-weight-bold">{{ item.bannerTitle }}</div>
          <div class="text-caption text-grey">{{ item.bannerSubtitle || '-' }}</div>
        </template>
        <template #item.exposeYn="{ item }">
          <v-chip size="small" :color="item.exposeYn === 'Y' ? 'primary' : 'grey'" variant="tonal">
            {{ item.exposeYn === 'Y' ? '노출' : '미노출' }}
          </v-chip>
        </template>
        <template #item.displaySeconds="{ item }">{{ item.displaySeconds || 30 }}초</template>
        <template #item.createdDt="{ item }">{{ formatDate(item.createdDt) }}</template>
        <template #item.actions="{ item }">
          <v-btn icon="mdi-pencil-outline" size="small" variant="text" @click="openEdit(item)" />
          <v-btn icon="mdi-delete-outline" size="small" variant="text" color="error" @click="removeBanner(item)" />
        </template>
      </v-data-table>
    </v-card>

    <v-dialog v-model="dialog" max-width="760" persistent>
      <v-card rounded="lg">
        <v-card-title>{{ dialogTitle }}</v-card-title>
        <v-card-text>
          <v-row dense>
            <v-col cols="12" md="8">
              <v-text-field v-model="form.bannerTitle" label="배너 제목 *" variant="outlined" density="comfortable" />
            </v-col>
            <v-col cols="12" md="4">
              <v-select v-model="form.exposeYn" :items="ynOptions" label="노출 여부" variant="outlined" density="comfortable" />
            </v-col>
            <v-col cols="12">
              <v-text-field v-model="form.bannerSubtitle" label="배너 부제목" variant="outlined" density="comfortable" />
            </v-col>
            <v-col cols="12" md="4">
              <v-text-field v-model.number="form.exposeOrder" label="노출 순서" type="number" variant="outlined" density="comfortable" />
            </v-col>
            <v-col cols="12" md="4">
              <v-text-field v-model.number="form.displaySeconds" label="노출 시간(초)" type="number" min="1" variant="outlined" density="comfortable" />
            </v-col>
            <v-col cols="12" md="4">
              <v-text-field v-model="form.altText" label="대체 문구" variant="outlined" density="comfortable" />
            </v-col>
          </v-row>

          <div class="banner-upload-block">
            <div class="d-flex align-center justify-space-between mb-3">
              <div>
                <div class="text-subtitle-2 font-weight-bold">배너 이미지</div>
                <div class="text-caption text-grey">{{ imageSpec }}</div>
                <div class="text-caption text-grey">
                  사용자 메인 상단 비율로 미리보기됩니다.
                  <router-link to="/media-hub/image-guide" class="ml-1">제작 가이드</router-link>
                </div>
              </div>
              <div class="d-flex ga-2">
                <input
                  ref="imageInput"
                  type="file"
                  accept="image/jpeg,image/png,image/gif,image/webp,.jpg,.jpeg,.png,.gif,.webp"
                  hidden
                  @change="selectImage"
                >
                <v-btn size="small" variant="tonal" prepend-icon="mdi-image-plus" @click="$refs.imageInput?.click()">이미지 선택</v-btn>
                <v-btn v-if="previewUrl" size="small" variant="text" color="error" prepend-icon="mdi-delete-outline" @click="removeImage">삭제</v-btn>
              </div>
            </div>
            <div v-if="previewUrl" class="banner-preview">
              <img :src="previewUrl" alt="" />
              <div class="banner-preview-dim" />
              <div class="banner-preview-copy">
                <strong>{{ form.bannerTitle || '배너 제목' }}</strong>
                <span>{{ form.bannerSubtitle || '배너 부제목이 이 위치에 표시됩니다.' }}</span>
              </div>
            </div>
            <div v-else class="banner-empty">이미지를 선택하면 미리보기가 표시됩니다.</div>
          </div>
        </v-card-text>
        <v-card-actions>
          <v-spacer />
          <v-btn variant="text" @click="dialog = false">취소</v-btn>
          <v-btn color="primary" :loading="saving" @click="saveBanner">저장</v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>
  </div>
</template>

<style scoped>
.banner-thumb {
  display: flex;
  width: 120px;
  height: 56px;
  align-items: center;
  justify-content: center;
  overflow: hidden;
  border: 1px solid rgba(255, 255, 255, 0.08);
  border-radius: 8px;
  background: rgba(255, 255, 255, 0.04);
}

.banner-thumb img {
  width: 100%;
  height: 100%;
  object-fit: cover;
}

.banner-upload-block {
  border: 1px solid rgba(205, 178, 122, 0.2);
  border-radius: 10px;
  padding: 14px;
  background: rgba(255, 255, 255, 0.03);
}

.banner-preview {
  position: relative;
  overflow: hidden;
  border-radius: 10px;
  background: #101722;
  aspect-ratio: 1680 / 690;
}

.banner-preview img {
  display: block;
  width: 100%;
  height: 100%;
  object-fit: cover;
}

.banner-preview-dim {
  position: absolute;
  inset: 0;
  background: linear-gradient(180deg, rgba(0, 0, 0, 0), rgba(0, 0, 0, 0.72));
}

.banner-preview-copy {
  position: absolute;
  left: 8%;
  bottom: 14%;
  display: flex;
  max-width: 70%;
  flex-direction: column;
  gap: 8px;
  color: #fff;
}

.banner-preview-copy strong {
  font-size: clamp(20px, 4vw, 42px);
  line-height: 1.15;
}

.banner-preview-copy span {
  font-size: clamp(12px, 1.8vw, 18px);
  line-height: 1.5;
}

.banner-empty {
  display: flex;
  min-height: 160px;
  align-items: center;
  justify-content: center;
  border: 1px dashed rgba(205, 178, 122, 0.28);
  border-radius: 8px;
  color: #9aa7b8;
}
</style>
